import React from "react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "./ui/sheet";
import { Button } from "./ui/button";
import { Star, Trash2, ExternalLink, Users, Gauge } from "lucide-react";
import { useFavorites } from "../context/FavoritesContext";
import { useGame } from "../context/GameContext";
import { useAuth } from "../context/AuthContext";
import PingIndicator from "./PingIndicator";
import { lastSeenShort } from "../lib/format";

const FavoritesSheet = ({ open, onOpenChange, onOpenServer }) => {
  const { favorites, toggleFavorite, authed } = useFavorites();
  const { games, setGameId } = useGame();
  const { user, login } = useAuth();

  const grouped = favorites.reduce((acc, f) => {
    const g = f.game_id || "other";
    acc[g] = acc[g] || [];
    acc[g].push(f);
    return acc;
  }, {});

  const gameName = (gid) => games.find((g) => g.id === gid)?.name || gid;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-md overflow-y-auto"
        style={{ background: "var(--bg-surface)", borderColor: "var(--primary-accent)", borderLeftWidth: 1 }}
        data-testid="favorites-sheet"
      >
        <SheetHeader>
          <SheetTitle className="font-head text-2xl uppercase tracking-wider flex items-center gap-2" style={{ color: "var(--text-primary)" }}>
            <Star className="w-5 h-5" style={{ color: "var(--highlight)", fill: "var(--highlight)" }} />
            Favorites
          </SheetTitle>
          <div className="hud-caption">
            {authed
              ? "Synced across all your devices"
              : "Stored locally — login to sync across devices"}
          </div>
        </SheetHeader>

        {!authed && (
          <div className="tac-card p-4 mt-4" style={{ background: "var(--bg-main)" }}>
            <div className="hud-label mb-2">OPERATOR OFFLINE</div>
            <p className="text-sm mb-3" style={{ color: "var(--text-secondary)" }}>
              Log in with Google to sync favorites across devices and add custom Minecraft servers.
            </p>
            <Button
              onClick={login}
              data-testid="fav-login-btn"
              className="font-mono-hud tracking-widest"
              style={{ background: "var(--primary-accent)", color: "#000", borderRadius: 2 }}
            >
              LOGIN WITH GOOGLE
            </Button>
          </div>
        )}

        {favorites.length === 0 && (
          <div className="mt-8 text-center" data-testid="favorites-empty">
            <Star className="w-10 h-10 mx-auto" style={{ color: "var(--text-dim)" }} />
            <div className="hud-caption mt-3">NO FAVORITES YET</div>
            <p className="text-sm mt-2" style={{ color: "var(--text-secondary)" }}>
              Tap the ★ on any server row to pin it here.
            </p>
          </div>
        )}

        <div className="space-y-6 mt-6">
          {Object.entries(grouped).map(([gid, favs]) => (
            <div key={gid}>
              <div className="hud-label mb-2 flex items-center justify-between">
                <span>{gameName(gid)}</span>
                <span style={{ color: "var(--primary-accent)" }}>{favs.length}</span>
              </div>
              <div className="space-y-2">
                {favs.map((f) => {
                  const s = f.server;
                  return (
                    <div
                      key={f.server_id}
                      className="tac-card p-3 cursor-pointer"
                      style={{ background: "var(--bg-main)" }}
                      data-testid={`fav-entry-${f.server_id}`}
                      onClick={() => {
                        setGameId(gid);
                        if (s) onOpenServer(s);
                        onOpenChange(false);
                      }}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0 flex-1">
                          <div className="font-head uppercase font-bold tracking-wide truncate text-sm" style={{ color: "var(--text-primary)" }}>
                            {s?.name || f.server_id}
                          </div>
                          <div className="font-mono-hud text-[11px] mt-0.5" style={{ color: "var(--text-dim)" }}>
                            {f.server_id}
                            {s && ` · ${s.region}`}
                          </div>
                          {s && (
                            <div className="flex items-center gap-3 mt-2 text-xs flex-wrap">
                              <span className="flex items-center gap-1 font-mono-hud" style={{ color: "var(--text-primary)" }}>
                                <Users className="w-3 h-3" />
                                {s.online ? `${s.players}/${s.max_players}` : `— / ${s.max_players}`}
                              </span>
                              <PingIndicator ping={s.ping} online={s.online} server={s} />
                              <span className="hud-label" style={{ color: s.online ? "var(--highlight)" : "var(--ping-high)" }}>
                                {s.online ? "ONLINE" : "OFFLINE"}
                              </span>
                              {!s.online && s.last_online_at && (
                                <span className="font-mono-hud text-[10px] tracking-widest" style={{ color: "var(--text-dim)" }}>
                                  LAST {lastSeenShort(s.last_online_at)}
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                        <div className="flex flex-col gap-1 shrink-0">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 w-7 p-0"
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleFavorite({ id: f.server_id, game_id: f.game_id });
                            }}
                            data-testid={`fav-remove-${f.server_id}`}
                            style={{ color: "var(--secondary-accent)" }}
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 w-7 p-0"
                            style={{ color: "var(--text-secondary)" }}
                            onClick={(e) => e.stopPropagation()}
                            data-testid={`fav-open-${f.server_id}`}
                          >
                            <ExternalLink className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default FavoritesSheet;
